import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AssistantDocumentDetails,
  AssistantFeedback,
  AssistantFilter,
  AssistantSearch,
  AssistantSuggestData,
} from '@vi-iot/client/apis/assistant/assistant.api-model';
import { Observable } from 'rxjs';
import { StatusLoadedValue } from '../app.state.models';
import { AssistantAction } from './assistant.action';
import {
  AppServiceType,
  ASSISTANT_DOCUMENT_LIMIT,
  AssistantDocumentsState,
  AssistantFeedbackPredefinedRanges,
  AssistantFeedbackSortOrder,
  AssistantFeedbackState,
  AssistantFilterItem,
  AssistantFilterPayload,
  AssistantFilterSection,
  AssistantOverlayState,
  AssistantOverlayTypeEnum,
  SelectedDocumentsState,
} from './assistant.model';
import { AssistantSelectors } from './assistant.selectors';

@Injectable({ providedIn: 'root' })
export class AssistantStore {
  constructor(private store: Store) {}

  filters$: Observable<StatusLoadedValue<AssistantFilter[]>> = this.store.select(
    AssistantSelectors.selectFilters
  );
  searchQuery$: Observable<string> = this.store.select(AssistantSelectors.getSearchQuery);
  selectedFilters$: Observable<AssistantFilterItem[]> = this.store.select(
    AssistantSelectors.getSelectedFilters
  );
  getAssistantFeedbackData$: Observable<AssistantFeedback[]> = this.store.select(
    AssistantSelectors.selectAssistantFeedbackData
  );
  filterDropdownOptions$: Observable<AssistantFilterSection[]> = this.store.select(
    AssistantSelectors.getFilterDropdownOptions
  );
  suggestions$: Observable<StatusLoadedValue<AssistantSuggestData[]>> = this.store.select(
    AssistantSelectors.selectSuggestions
  );
  searchDocuments$: Observable<AssistantDocumentsState> = this.store.select(
    AssistantSelectors.getSearchDocuments
  );
  selectedDocumentState$: Observable<SelectedDocumentsState> = this.store.select(
    AssistantSelectors.getSelectedDocumentState
  );
  searchDocument$: Observable<AssistantSearch> = this.store.select(
    AssistantSelectors.getSearchDocumentFromSelectedDocument
  );
  selectedDocumentDetails$: Observable<
    StatusLoadedValue<AssistantDocumentDetails>
  > = this.store.select(AssistantSelectors.getSelectedDocumentDetails);
  selectedDocumentContent$: Observable<StatusLoadedValue<string>> = this.store.select(
    AssistantSelectors.getSelectedDocumentContent
  );
  isFiltersOrDocumentsLoading$: Observable<boolean> = this.store.select(
    AssistantSelectors.isFiltersOrDocumentsLoading
  );

  assistantFeedbackState$: Observable<AssistantFeedbackState> = this.store.select(
    AssistantSelectors.selectAssistantFeedbackState
  );

  getOverlay$: Observable<AssistantOverlayState> = this.store.select(
    AssistantSelectors.selectOverlay
  );

  isSelectedDocumentContentError$: Observable<boolean> = this.store.select(
    AssistantSelectors.selectIsSelectedDocumentContentError
  );

  getFilters(
    app: AppServiceType,
    searchText: string = null,
    filtersPayload: AssistantFilterPayload = null,
    init = false
  ): void {
    this.store.dispatch(
      AssistantAction.getFilters.attempt({ searchText, filtersPayload, init, app })
    );
  }

  applyAssistantFilters(filters: AssistantFilterItem[]): void {
    this.store.dispatch(AssistantAction.setAssistantFilters.attempt({ filters }));
  }

  applyAssistantSearchQuery(query: string): void {
    this.store.dispatch(AssistantAction.setSearchQuery.attempt({ query }));
  }

  getSuggestions(query: string, app: AppServiceType): void {
    this.store.dispatch(AssistantAction.getSuggestions.attempt({ query, app }));
  }

  searchForDocuments(
    text: string,
    filtersPayload: AssistantFilterPayload,
    app: AppServiceType,
    limit = ASSISTANT_DOCUMENT_LIMIT
  ): void {
    this.store.dispatch(
      AssistantAction.searchForDocuments.attempt({ text, filtersPayload, limit, app })
    );
  }

  fetchDocuments(app: AppServiceType, limit = 10): void {
    this.store.dispatch(AssistantAction.searchForDocuments.fetch({ maxItemsPerFetch: limit, app }));
  }

  searchForFeedbackDocuments(): void {
    this.store.dispatch(AssistantAction.assistantFeedbackData.attempt());
  }

  fetchFeedbackDocuments(limit = 100): void {
    this.store.dispatch(AssistantAction.assistantFeedbackData.fetch({ maxItemsPerFetch: limit }));
  }

  setFeedbackSearch(search: string): void {
    this.store.dispatch(AssistantAction.assistantFeedbackData.setSearch({ search }));
  }

  setFeedbackFilterData(
    filter: AssistantFeedbackPredefinedRanges = null,
    startDate: string = null,
    endDate: string = null,
    sortOrder: AssistantFeedbackSortOrder = null
  ): void {
    this.store.dispatch(
      AssistantAction.assistantFeedbackData.setFilterData({ filter, startDate, endDate, sortOrder })
    );
  }

  clearFeedbackState(): void {
    this.store.dispatch(AssistantAction.assistantFeedbackData.clear());
  }

  getDocumentDetails(documentId: string, app: AppServiceType): void {
    this.store.dispatch(AssistantAction.selectDocument.getDocumentDetails({ documentId, app }));
  }

  getDocumentDetailsWithUrlRedirection(documentId: string, app: AppServiceType): void {
    this.store.dispatch(
      AssistantAction.selectDocument.getDocumentDetailsWithUrlRedirection({ documentId, app })
    );
  }

  getDocumentContent(app: AppServiceType): void {
    this.store.dispatch(AssistantAction.selectDocument.getContent({ app }));
  }

  getDocumentContentById(documentId: string, app: AppServiceType): void {
    this.store.dispatch(AssistantAction.selectDocument.getContentByDocumentId({ documentId, app }));
  }

  clearSearch(): void {
    this.store.dispatch(AssistantAction.clearSearch());
  }

  selectDocument(document: AssistantSearch): void {
    this.store.dispatch(AssistantAction.selectDocument.setSearchDocument({ document }));
  }

  clearSelectedDocumentState(): void {
    this.store.dispatch(AssistantAction.selectDocument.clearState());
  }

  setOverlay(overlay: AssistantOverlayTypeEnum): void {
    this.store.dispatch(AssistantAction.changeOverlay({ overlay }));
  }

  closeOverlay(): void {
    this.store.dispatch(
      AssistantAction.changeOverlay({ overlay: AssistantOverlayTypeEnum.SearchView })
    );
  }

  clearFilters(): void {
    this.store.dispatch(AssistantAction.setAssistantFilters.clear());
  }

  setInitialState(): void {
    this.store.dispatch(AssistantAction.setInitialState());
  }
}
